/* Copyright (c) 2018-2025, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { Inject, Injectable, Optional } from '@angular/core';
import { HttpHeaders } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { NvHttpEndpointFactoryService } from 'nv-angular-http-endpoint/lib/nv-http-endpoint-factory/nv-http-endpoint-factory.service';
import { NvHttpEndpoint, PropertyBag } from 'nv-angular-http-endpoint/lib/nv-http-endpoint/nv-http-endpoint';
import { DistributedTracingUtilService } from 'distributed-tracing/lib/services/distributedtracing-util.service';
import { OpenTelemetrySpan } from 'distributed-tracing/lib/distributed-tracing';
import { SpanNames } from 'distributed-tracing/lib/constants/distributedtracing_constants';
import { PROXY_CONFIG } from 'proxy-config/lib/proxy.config.provider';
import { ProxyConfigType } from 'proxy-config/lib/proxy.config';
import { JsEventsServerConfig } from '../config/js-events.config';
import { RequestParams } from '../models/common-data.models';

@Injectable({
    providedIn: 'root'
})
export class EventsSdkService {
    private eventServerEndpoint: NvHttpEndpoint;
    private server: string;
    constructor(
        private sdkConfig: JsEventsServerConfig,
        private endpointFactory: NvHttpEndpointFactoryService,
        private distributedTracingUtilService: DistributedTracingUtilService,
        @Optional() @Inject(PROXY_CONFIG) proxyConfig: ProxyConfigType
    ) {
        this.eventServerEndpoint = this.endpointFactory.createEndpoint({
            headers: {
                'Content-Type': 'application/json'
            },
            config: this.sdkConfig.retryConfig,
            reportRequest: false
        });
        if (proxyConfig?.useProxy) {
            this.server = proxyConfig.proxyUrl;
        } else {
            this.server = this.sdkConfig.server;
        }
    }

    private generateUrl(url: string) {
        return this.server + '/' + this.sdkConfig.version + url;
    }

    sendEvent(
        params: RequestParams,
        eventData: string,
        isImmediateRequest: boolean,
        parentSpan?: OpenTelemetrySpan
    ): Observable<any> {
        if (params !== null && params.hasOwnProperty('sync')) {
            // TODO use params.sync for internal purpose before makeRequest
            delete params.sync;
        }
        const clientHeaders: HttpHeaders | PropertyBag = {
            'Content-Type': 'application/json'
        };
        const options = {
            body: eventData,
            headers: clientHeaders,
            params: {
                immediateRequest: JSON.stringify(isImmediateRequest)
            }
        };
        const httpRequest = this.eventServerEndpoint.makeRequestEx<object>(
            'POST',
            this.generateUrl('/events/json'),
            options
        );
        return this.distributedTracingUtilService.handleHttpEndpointSpan(
            httpRequest,
            options,
            SpanNames.SendTelemetryEvent,
            parentSpan
        );
    }

    sendEventviaBeacon(
        params: RequestParams,
        eventData: string,
        isImmediateRequest: boolean,
        parentSpan?: OpenTelemetrySpan
    ): Observable<any> {
        if (params?.hasOwnProperty('sync')) {
            // TODO use params.sync for internal purpose before makeRequest
            delete params.sync;
        }

        const formData = new Blob([eventData], { type: 'text/plain' });

        navigator.sendBeacon(this.generateUrl('/events/json'), formData);
        return of(true);
    }

    setServer(serverUrl: string): string {
        this.sdkConfig.server = serverUrl;
        return this.getServerLocale(null);
    }

    getServerLocale(input: string) {
        const serverToParse = input || this.sdkConfig.server;
        if (!serverToParse || serverToParse === '') {
            return 'unknown';
        } else if (serverToParse.endsWith('.cn')) {
            return 'china';
        } else {
            return 'global';
        }
    }
}
